import { NavLink } from 'react-router-dom'
import { FlaskConical, LayoutGrid, Settings, ShieldCheck, Zap } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useBooks } from '../hooks/useBooks'
import type { Book, BookRole } from '../types'
import './MobileBottomNav.css'

const ROLE_ORDER: BookRole[] = ['research', 'validation', 'live']
const ROLE_LABEL: Record<BookRole, string> = {
  research: 'Research',
  validation: 'Validation',
  live: 'Live',
  book: 'Book',
}
const ROLE_ICON: Record<BookRole, LucideIcon> = {
  research: FlaskConical,
  validation: ShieldCheck,
  live: Zap,
  book: FlaskConical,
}

interface BookTabProps {
  role: BookRole
  isLoading: boolean
  isError: boolean
  book: Book | undefined
}

/**
 * One role-scoped tab. Three non-resolved states are locked (D2 point 3),
 * distinct from each other and from the resolved states, mirroring
 * BookSwitcher's dormant-pill semantics:
 *  - isLoading:  enabled-pending — dimmed, not a link (no navigation), and
 *                shows the real role label (never a "not created" claim
 *                while the roster hasn't resolved yet).
 *  - isError:    labeled "unavailable" — visually and textually distinct
 *                from "dormant" so an API failure never reads as "this
 *                book was never built."
 *  - resolved, absent: dormant — dimmed, `title="not yet created"`,
 *                matching BookSwitcher's disabled ghost pill.
 *  - resolved, present: a real NavLink to `/book/<id>`. Never
 *                `/book/undefined` — this branch only renders once `book`
 *                is a real object with an `id`.
 */
function BookTab({ role, isLoading, isError, book }: BookTabProps) {
  const Icon = ROLE_ICON[role]
  const label = ROLE_LABEL[role]

  if (isLoading) {
    return (
      <span className="mobile-nav-tab mobile-nav-tab--pending" aria-disabled="true">
        <Icon size={20} aria-hidden={true} />
        <span className="mobile-nav-tab-label">{label}</span>
      </span>
    )
  }

  if (isError) {
    return (
      <span
        className="mobile-nav-tab mobile-nav-tab--error"
        title="unavailable"
        aria-disabled="true"
      >
        <Icon size={20} aria-hidden={true} />
        <span className="mobile-nav-tab-label">unavailable</span>
      </span>
    )
  }

  if (!book) {
    return (
      <span
        className="mobile-nav-tab mobile-nav-tab--dormant"
        title="not yet created"
        aria-disabled="true"
      >
        <Icon size={20} aria-hidden={true} />
        <span className="mobile-nav-tab-label">{label}</span>
      </span>
    )
  }

  return (
    <NavLink
      to={`/book/${book.id}`}
      className={({ isActive }) => `mobile-nav-tab${isActive ? ' active' : ''}`}
    >
      <Icon size={20} aria-hidden={true} />
      <span className="mobile-nav-tab-label">{label}</span>
    </NavLink>
  )
}

/**
 * Fixed bottom tab bar for the mobile shell (D2 point 3): Fleet + one tab
 * per book role (fixed order) + Ops. z-index 80 — below MobileTimeframeRow
 * (90) and MobileHeader (100), and well below RunDetailDrawer (200) /
 * .modal-overlay (1000) (WARGAME 18 D2 z-scale lock).
 */
export function MobileBottomNav() {
  const { isLoading, isError, byRole } = useBooks()

  return (
    <nav className="mobile-bottom-nav" aria-label="Primary">
      <NavLink
        to="/"
        end
        className={({ isActive }) => `mobile-nav-tab${isActive ? ' active' : ''}`}
      >
        <LayoutGrid size={20} aria-hidden={true} />
        <span className="mobile-nav-tab-label">Fleet</span>
      </NavLink>

      {ROLE_ORDER.map((role) => (
        <BookTab key={role} role={role} isLoading={isLoading} isError={isError} book={byRole(role)} />
      ))}

      <NavLink
        to="/ops"
        className={({ isActive }) => `mobile-nav-tab${isActive ? ' active' : ''}`}
      >
        <Settings size={20} aria-hidden={true} />
        <span className="mobile-nav-tab-label">Ops</span>
      </NavLink>
    </nav>
  )
}
